import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  GrupoDeAcesso,
  GrupoDeAcessoDetalhe,
  TelaInicialDisponivel,
  atualizarGrupoDeAcesso,
  buscarGrupoDeAcesso,
  criarGrupoDeAcesso,
  definirUsuariosDoGrupo,
  excluirGrupoDeAcesso,
  listarGruposDeAcesso,
  listarTelasIniciais,
} from '../../../api/gruposDeAcessoApi';
import { getUsers, User } from '../../../api/usersApi';
import { notify } from '../../../shared/feedback/notify';
import { ehPapelFinanceiroExclusivo, exclusivoSelecionado } from '../../../shared/papeis';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Casco da página E a grade da tabela (`TabelaScroll`/`TabelaContent`/`Item`/`Data`) — é o
// padrão de listagem do sistema, o mesmo da Recepção e da tela de Usuários.
import '../../recepcao/pages/Recepcao.css';
// `data-link`: o nome clicável da linha, como a placa na Recepção.
import '../../user/pages/user.css';
// Selo de situação (Ativo/Inativo) — paleta compartilhada, nunca classe de status por tela.
import '../../../shared/ui/badge-status.css';
import './css/adminSistema.css';
import GrupoDeAcessoModal from '../components/GrupoDeAcessoModal';
import UsuariosDoGrupoModal from '../components/UsuariosDoGrupoModal';

const CABECALHOS = [
  'Grupo',
  'Perfis que concede',
  'Tela inicial',
  'Pessoas',
  'Situação',
  'Ações',
] as const;

/**
 * Larguras da grade (padrão `--table-columns` da Recepção). Fixas em vez de medidas pelo
 * conteúdo: a lista é curta e previsível, e medir exigiria o `useTableColumns` da tela de
 * Usuários, que existe para tabelas de 11 colunas com dado variável.
 */
const COLUNAS_DA_TABELA = '260px 300px 220px 100px 120px 240px';

/** Todos os perfis do sistema, com o nome que aparece na tela. */
export const PAPEIS_DO_SISTEMA: Array<{ valor: string; rotulo: string }> = [
  // Ordem alfabética pelo RÓTULO (pedido do Victor, 25/09/2026).
  { valor: 'ADMINISTRACAO', rotulo: 'Administração' },
  { valor: 'ADMIN', rotulo: 'Administrador' },
  { valor: 'CHEFE_DE_OFICINA', rotulo: 'Chefe de Oficina' },
  { valor: 'COLABORADOR', rotulo: 'Colaborador' },
  { valor: 'COMISSIONAMENTO', rotulo: 'Comissionamento' },
  { valor: 'COMPRA', rotulo: 'Compras' },
  { valor: 'CONSULTOR', rotulo: 'Consultor' },
  { valor: 'CONTAC', rotulo: 'Contac' },
  { valor: 'ESTOQUE', rotulo: 'Estoque' },
  { valor: 'GESTAO_COMPRAS', rotulo: 'Gestão de Compras' },
  { valor: 'INSPETOR', rotulo: 'Inspetor' },
  { valor: 'TELAO', rotulo: 'Telão' },
  { valor: 'ORCAMENTO', rotulo: 'Orçamento' },
  { valor: 'RASTREADOR', rotulo: 'Rastreador' },
  { valor: 'RECEPCAO', rotulo: 'Recepção' },
  { valor: 'REGIONAL', rotulo: 'Regional' },
  { valor: 'RELACIONAMENTO', rotulo: 'Relacionamento' },
  { valor: 'EVENTOS', rotulo: 'Setor de Eventos' },
  { valor: 'TI', rotulo: 'TI' },
];

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é um grupo de acesso',
    conteudo: (
      <>
        <p>
          Um grupo é um <strong>conjunto de perfis com nome</strong>. Em vez de marcar perfil por
          perfil em cada pessoa, você monta o grupo uma vez ("Consultoria de Relacionamento" =
          Comissionamento + Relacionamento) e coloca as pessoas nele.
        </p>
        <p>
          Quem está em um grupo <strong>soma</strong> os perfis dele aos que já tem. Uma pessoa
          pode estar em vários grupos: o acesso dela é a união de tudo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quando a mudança passa a valer',
    conteudo: (
      <p>
        No <strong>próximo login</strong> da pessoa. O acesso viaja no crachá digital que o sistema
        emite na entrada, e ele vale por até 2 horas — então quem já está com o sistema aberto
        continua com o acesso anterior até sair e entrar de novo. Não é atraso do sistema: é o
        mesmo funcionamento do código de consultor e de regional.
      </p>
    ),
  },
  {
    titulo: 'Perfis que não podem ser combinados',
    conteudo: (
      <>
        <p>
          Os quatro perfis financeiros — <strong>Contac</strong>, <strong>Consultor</strong>,{' '}
          <strong>Regional</strong> e <strong>Relacionamento</strong> — mostram os mesmos números
          de jeitos diferentes: um vê todos os valores, outro nunca vê valor nenhum, e os outros
          dois veem só a própria carteira. Por isso <strong>não podem ser combinados</strong> entre
          si; ao marcar um, os outros três ficam indisponíveis.
        </p>
        <p>
          Combinar qualquer um deles com perfis da Oficina é permitido — é justamente para isso que
          os grupos existem.
        </p>
      </>
    ),
  },
  {
    titulo: 'Tela inicial do grupo',
    conteudo: (
      <p>
        Define para onde a pessoa vai ao entrar. Se ela estiver em mais de um grupo com tela
        definida, vale a do grupo de <strong>maior prioridade</strong> (o número que você informa).
        Deixando em branco, o sistema escolhe pela regra de sempre, a partir dos perfis dela.
      </p>
    ),
  },
  {
    titulo: 'Grupo inativo e exclusão',
    conteudo: (
      <>
        <p>
          <strong>Desativar</strong> um grupo tira os perfis dele de todo mundo, mas mantém a lista
          de quem estava lá — é a forma de suspender um acesso sem perder a configuração.
        </p>
        <p>
          <strong>Excluir</strong> só é possível com o grupo vazio. Se ainda houver pessoas nele, o
          sistema recusa e diz quem seria afetado: elas perderiam esses acessos sem aviso.
        </p>
      </>
    ),
  },
  {
    titulo: 'Se você é do TI',
    conteudo: (
      <p>
        O TI gerencia grupos normalmente, com uma exceção: <strong>grupo que concede o perfil de
        Administrador</strong>. Criar, editar, excluir ou colocar gente num grupo desses é
        exclusivo do Administrador.
      </p>
    ),
  },
];

/**
 * Grupos de Acesso (at-81, ADR 0044) — a tela onde o ADMIN/TI gerencia conjuntos de perfis.
 *
 * ⚠️ Sem CSS próprio: monta-se por reuso (casco do `Recepcao.css`, cards/tabelas/botões do
 * `adminSistema.css`), como as telas de sincronização já fazem.
 */
const GruposDeAcessoPage: React.FC = () => {
  const [grupos, setGrupos] = useState<GrupoDeAcesso[]>([]);
  const [telas, setTelas] = useState<TelaInicialDisponivel[]>([]);
  const [colaboradores, setColaboradores] = useState<User[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroDeCarga, setErroDeCarga] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const [grupoEmEdicao, setGrupoEmEdicao] = useState<GrupoDeAcesso | null>(null);
  const [criandoGrupo, setCriandoGrupo] = useState(false);
  const [grupoDeUsuarios, setGrupoDeUsuarios] = useState<GrupoDeAcessoDetalhe | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, telasDisponiveis] = await Promise.all([
        listarGruposDeAcesso(),
        listarTelasIniciais(),
      ]);
      setGrupos(lista);
      setTelas(telasDisponiveis);
      setErroDeCarga(false);
    } catch (error) {
      // 🔴 Estado seguro E sinal: lista vazia por erro é indistinguível de "nenhum grupo
      // criado ainda", e a segunda leitura convida o ADMIN a recriar o que já existe.
      setGrupos([]);
      setErroDeCarga(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os grupos de acesso. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Usuários para o seletor do grupo — carregados sob demanda, na abertura do modal. */
  const carregarUsuarios = useCallback(async () => {
    if (colaboradores.length > 0) return;
    try {
      const { users } = await getUsers({ page: 1, limit: 500, status: 'ATIVOS' });
      setColaboradores(users);
    } catch (error) {
      notify.errorFrom(
        error,
        'Não foi possível carregar a lista de usuários. Feche e abra novamente; se persistir, acione a TI.',
      );
    }
  }, [colaboradores.length]);

  const abrirUsuarios = async (grupo: GrupoDeAcesso) => {
    try {
      const [detalhe] = await Promise.all([buscarGrupoDeAcesso(grupo.id), carregarUsuarios()]);
      setGrupoDeUsuarios(detalhe);
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível abrir os usuários deste grupo.');
    }
  };

  const salvarGrupo = async (payload: any, id?: string) => {
    try {
      if (id) {
        await atualizarGrupoDeAcesso(id, payload);
        notify.actionSuccess({ action: 'update', subject: 'grupo de acesso' });
      } else {
        await criarGrupoDeAcesso(payload);
        notify.actionSuccess({ action: 'create', subject: 'grupo de acesso' });
      }
      setGrupoEmEdicao(null);
      setCriandoGrupo(false);
      await carregar();
    } catch (error) {
      // Não fecha o modal: o que a pessoa digitou continua na tela para ser corrigido.
      notify.actionError(error, { action: id ? 'update' : 'create', subject: 'grupo de acesso' });
    }
  };

  const excluir = async (grupo: GrupoDeAcesso) => {
    try {
      await excluirGrupoDeAcesso(grupo.id);
      notify.actionSuccess({ action: 'delete', subject: 'grupo de acesso' });
      await carregar();
    } catch (error) {
      notify.actionError(error, { action: 'delete', subject: 'grupo de acesso' });
    }
  };

  const salvarUsuarios = async (id: string, usuarioIds: string[]) => {
    try {
      await definirUsuariosDoGrupo(id, usuarioIds);
      notify.success('Usuários do grupo atualizados. O acesso vale no próximo login de cada um.');
      setGrupoDeUsuarios(null);
      await carregar();
    } catch (error) {
      notify.actionError(error, { action: 'update', subject: 'grupo de acesso' });
    }
  };

  const totalDePessoas = useMemo(
    () => grupos.reduce((soma, grupo) => soma + grupo.quantidadeUsuarios, 0),
    [grupos],
  );

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        {/* `gap: 8` alinhado ao padrão das outras telas (ConsumoIaPage, Auditoria) — sem ele o
            "?" fica colado no título. */}
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Grupos de Acesso
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>

        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={() => setCriandoGrupo(true)}
        >
          Novo grupo
        </button>
      </div>

      {erroDeCarga && (
        <p className="erro-de-tela">
          Não foi possível carregar os grupos de acesso. Pode haver grupos que não estão sendo
          exibidos — recarregue a página antes de criar um novo.
        </p>
      )}

      <div className="admin-card">
        <p className="admin-hint">
          Um grupo concede um conjunto de perfis a várias pessoas de uma vez. O acesso de cada
          pessoa é a soma dos perfis dela com os dos grupos em que ela está, e passa a valer no
          próximo login.
        </p>

        {carregando && <p className="admin-texto-secundario">Carregando grupos...</p>}

        {!carregando && !erroDeCarga && grupos.length === 0 && (
          <p className="admin-vazio">
            Nenhum grupo de acesso criado ainda. Crie o primeiro para conceder perfis a várias
            pessoas de uma vez.
          </p>
        )}

        {!carregando && grupos.length > 0 && (
          <>
            {/*
              Tabela no padrão da Recepção (grade com `--table-columns`), o mesmo que a tela de
              Usuários usa. A `admin-table` da primeira versão é a tabela das ABAS de configuração
              — dentro de uma tela de listagem ela destoa das demais do sistema.
            */}
            <div className="TabelaScroll">
              <div
                className="TabelaContent"
                style={{ ['--table-columns' as any]: COLUNAS_DA_TABELA }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {CABECALHOS.map((cabecalho) => (
                        <div className="Opcoes" key={cabecalho}>
                          {cabecalho}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {grupos.map((grupo) => {
                  const tela = telas.find((t) => t.path === grupo.telaInicial);
                  return (
                    <div className="Tabela" key={grupo.id}>
                      <div className="Item">
                        {/* Clicar no nome edita — mesmo gesto da placa na Recepção e do nome
                            na tela de Usuários. */}
                        <div
                          className="data-link"
                          title={grupo.descricao || grupo.nome}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setGrupoEmEdicao(grupo)}
                        >
                          {grupo.nome}
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data" title={grupo.papeisRotulos.join(', ')}>
                          {grupo.papeisRotulos.length === 0
                            ? 'Nenhum perfil'
                            : grupo.papeisRotulos.join(', ')}
                        </div>
                      </div>

                      <div className="Item">
                        <div
                          className="Data"
                          title={
                            grupo.telaInicial
                              ? `${tela?.rotulo ?? grupo.telaInicial} (prioridade ${grupo.prioridade})`
                              : 'Pela regra de perfil'
                          }
                        >
                          {grupo.telaInicial
                            ? `${tela?.rotulo ?? grupo.telaInicial} · ${grupo.prioridade}`
                            : 'Pela regra de perfil'}
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data">{grupo.quantidadeUsuarios}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">
                          <span
                            className={`cilia-badge ${grupo.ativo ? 'badge-verde' : 'badge-cinza'}`}
                          >
                            {grupo.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>

                      <div className="Item">
                        <div className="ItemAcoes">
                          <button
                            type="button"
                            className="admin-btn admin-btn-secondary"
                            onClick={() => abrirUsuarios(grupo)}
                          >
                            Pessoas
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-danger-outline"
                            onClick={() => excluir(grupo)}
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="admin-texto-secundario" style={{ marginTop: 12 }}>
              {grupos.length} grupo(s), {totalDePessoas} vínculo(s) de pessoa.
            </p>
          </>
        )}
      </div>

      {(criandoGrupo || grupoEmEdicao) && (
        <GrupoDeAcessoModal
          grupo={grupoEmEdicao}
          telas={telas}
          onFechar={() => {
            setCriandoGrupo(false);
            setGrupoEmEdicao(null);
          }}
          onSalvar={salvarGrupo}
        />
      )}

      {grupoDeUsuarios && (
        <UsuariosDoGrupoModal
          grupo={grupoDeUsuarios}
          usuarios={colaboradores}
          onFechar={() => setGrupoDeUsuarios(null)}
          onSalvar={salvarUsuarios}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Grupos de Acesso"
        secoes={helpSections}
      />
    </div>
  );
};

export default GruposDeAcessoPage;

/** Exportado para os modais: quais perfis ficam indisponíveis dada a seleção atual. */
export function papeisBloqueados(selecionados: string[]): string[] {
  const exclusivo = exclusivoSelecionado(selecionados);
  if (!exclusivo) return [];
  return PAPEIS_DO_SISTEMA.map((p) => p.valor).filter(
    (papel) => ehPapelFinanceiroExclusivo(papel) && papel !== exclusivo,
  );
}
